/* eslint-disable require-jsdoc */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import Grid from '@mui/material/Grid';
import { TextInput, Theme, Text, Button } from '@simplybenefits/sb-ui-library';
import { getInstreamNotifications, downloadInstreamNotificationFile } from '../../../../../actions/adjudication.js';
import NotificationsRow from './NotificationsRow.js';
import loadingGif from '../../../../../assets/loading.gif';

const LIMIT = 20;

const STATUS_OPTIONS = [ 'All', 'Failed', 'Sent', 'Not Notified' ];
const SORT_OPTIONS = [
    'Notification Sent Date',
    'Certificate Number',
    'Dentist (Unique Provider Number)',
    'Service Date'
];
const SORT_DIRECTION_OPTIONS = [ 'DESC', 'ASC' ];

const styles = {
    headerContainer: {
        height: 30,
        display: 'flex',
        flexDirection: 'row',
        marginBottom: 5,
        justifyContent: 'space-between'
    },
    firstBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '15%',
        padding: '0 20px'
    },
    centerBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '12%',
        justifyContent: 'center'
    },
    emailBlock: {
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        width: '20%',
        justifyContent: 'center'
    },
    lastBlock: {
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        width: '15%',
        padding: '0 20px'
    },
    iconContainer: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '5%',
        color: Theme.colors.mainTextGrey
    },
    loaderContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '60vh',
        borderRadius: '10px',
        backgroundColor: 'white'
    },
    scrollContainer: {
        maxHeight: '60vh',
        overflowY: 'auto'
    }
};

/**
 *
 * @param param0
 * @param param0.dispatch
 * @returns {React.JSX.Element}
 * @constructor
 */
const NotificationsList = ({ dispatch, refresh }) => {
    const [ notifications, setNotifications ] = useState([]);
    const [ query, setQuery ] = useState('');
    const [ status, setStatus ] = useState('All');
    const [ sortBy, setSortBy ] = useState('Notification Sent Date');
    const [ sortDirection, setSortDirection ] = useState('DESC');
    const [ offset, setOffset ] = useState(0);
    const [ isComplete, setIsComplete ] = useState(false);
    const [ isLoading, setIsLoading ] = useState(false);

    const fetchNotifications = useCallback(async (_query, _status, _sortBy, _sortDirection, nextOffset, append) => {
        setIsLoading(true);

        const data = await dispatch(getInstreamNotifications('electronicSubmissionRequests', _query, _status, _sortBy, _sortDirection, LIMIT, nextOffset));

        if (data) {
            setNotifications((prev) => append ? [ ...prev, ...data ] : data);
            setOffset(nextOffset);

            const _isComplete = data.length < LIMIT;
            setIsComplete(_isComplete);
        }

        setIsLoading(false);
    }, [ dispatch ]);

    const isInitialMount = useRef(true);

    useEffect(() => {
        fetchNotifications(query, status, sortBy, sortDirection, 0, false);
    }, []);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        setQuery('');
        setStatus('All');
        setSortBy('Notification Sent Date');
        setSortDirection('DESC');
        setOffset(0);
        setIsComplete(false);
        fetchNotifications('', 'All', 'Notification Sent Date', 'DESC', 0, false);
    }, [ refresh ]);

    const handleSearch = (searchTerm) => {
        setQuery(searchTerm);
        fetchNotifications(searchTerm, status, sortBy, sortDirection, 0, false);
    };

    const handleStatusChange = (e) => {
        const newStatus = e.target.value;
        setStatus(newStatus);
        fetchNotifications(query, newStatus, sortBy, sortDirection, 0, false);
    };

    const handleSortByChange = (e) => {
        const newSortBy = e.target.value;
        setSortBy(newSortBy);
        fetchNotifications(query, status, newSortBy, sortDirection, 0, false);
    };

    const handleSortDirectionChange = (e) => {
        const newSortDirection = e.target.value;
        setSortDirection(newSortDirection);
        fetchNotifications(query, status, sortBy, newSortDirection, 0, false);
    };

    return (
        <>
            <Grid
                container
                spacing={1}
                style={{ marginBottom: 20 }}
            >
                <Grid item xs={12} md={8} sx={{ display: 'flex', alignItems: 'center' }}>
                    <TextInput
                        label="Search"
                        fullWidth
                        value={ query }
                        variant="outlined"
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={ (e) => {
                            if (e.key === 'Enter') {
                                return handleSearch(e.target.value);
                            }

                            return Promise.resolve(false);
                        }}
                    />
                </Grid>

                <Grid item xs={12} md={4}>
                    <TextInput
                        label="Sort Direction"
                        fullWidth
                        value={sortDirection}
                        variant="outlined"
                        onChange={handleSortDirectionChange}
                        select
                        selectOptions={SORT_DIRECTION_OPTIONS}
                    />
                </Grid>

                {/* Row 2: Notification Status + Sort By */}
                <Grid item xs={12} md={6}>
                    <TextInput
                        label="Notification Status"
                        fullWidth
                        value={status}
                        variant="outlined"
                        onChange={handleStatusChange}
                        select
                        selectOptions={STATUS_OPTIONS}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <TextInput
                        label="Sort By"
                        fullWidth
                        value={sortBy}
                        variant="outlined"
                        onChange={handleSortByChange}
                        select
                        selectOptions={SORT_OPTIONS}
                    />
                </Grid>
            </Grid>

            {
                isLoading ?
                    <div style={styles.loaderContainer}>
                        <img src={loadingGif} alt="loading..." />
                    </div>
                    :
                    <>
                        <div style={ styles.headerContainer }>
                            <div style={ styles.firstBlock }>
                                <Text tiny>Dentist Name</Text>
                            </div>
                            <div style={ styles.emailBlock }>
                                <Text tiny>Dentist Email</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text tiny>Patient Name</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text tiny>Certificate #</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text tiny>Submitted</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text tiny>Service Date</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text tiny>Unique Provider #</Text>
                            </div>
                            <div style={ styles.centerBlock }>
                                <Text tiny>Notification Sent Date</Text>
                            </div>
                            <div style={ styles.lastBlock }>
                                <Text tiny align="right">Notification Status</Text>
                            </div>
                            <div style={ styles.iconContainer } />
                        </div>

                        <div style={ styles.scrollContainer }>
                            {
                                notifications.length === 0
                                    ? <Text large customStyle={{ textAlign: 'center', padding: '20px 0', color: Theme.colors.mainTextGrey }}>No Paper Dental Claims Found</Text>
                                    : notifications.map((row, index) => (
                                        <NotificationsRow
                                            key={index}
                                            row={row}
                                            onDownloadCsv={(type, batchID) => dispatch(downloadInstreamNotificationFile(type, batchID)) }
                                        />
                                    ))
                            }
                        </div>
                    </>
            }

            {
                !isComplete
                    ? <Button
                        onClick={() => fetchNotifications(query, status, sortBy, sortDirection, offset + LIMIT, true)}
                        color="primary"
                        marginTop={2}
                        fullWidth
                    >
                        Load More
                    </Button>
                    : null
            }
        </>
    );
};

const mapStateToProps = (state) => ({
    loader: state.loader
});

export default connect(mapStateToProps)(NotificationsList);